import React, { useState } from 'react';
import axios from 'axios';

const ContentReportModal = ({ 
  isOpen, 
  onClose, 
  contentType, 
  contentId, 
  reportedUserId = null,
  contentPreview = null 
}) => {
  const [reportCategory, setReportCategory] = useState('');
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [error, setError] = useState('');

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

  const reportCategories = [
    { value: 'harassment', label: 'Harassment or Bullying', description: 'Targeting individuals with harmful behavior' },
    { value: 'spam', label: 'Spam or Unwanted Content', description: 'Repetitive, irrelevant, or promotional content' },
    { value: 'inappropriate_content', label: 'Inappropriate Content', description: 'Content not suitable for a professional platform' },
    { value: 'hate_speech', label: 'Hate Speech', description: 'Content that promotes hatred or discrimination' },
    { value: 'false_information', label: 'False Information', description: 'Misleading or factually incorrect information' },
    { value: 'copyright_violation', label: 'Copyright Violation', description: 'Unauthorized use of copyrighted material' },
    { value: 'privacy_violation', label: 'Privacy Violation', description: 'Sharing personal information without consent' },
    { value: 'other', label: 'Other', description: 'Issue not covered by other categories' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!reportCategory) {
      setError('Please select a report category');
      return;
    }

    if (!description.trim() || description.trim().length < 10) {
      setError('Please provide a detailed reason (at least 10 characters)');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication required');
      }

      const reportData = {
        content_type: contentType,
        content_id: contentId,
        reported_user_id: reportedUserId,
        category: reportCategory,
        reason: description.trim()
      };

      await axios.post(`${BACKEND_URL}/api/alumni/report-content`, reportData, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      setSubmitSuccess(true);
      setTimeout(() => {
        onClose();
        resetForm();
      }, 2000);

    } catch (error) {
      console.error('Error submitting report:', error);
      setError(error.response?.data?.detail || 'Failed to submit report. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setReportCategory('');
    setDescription('');
    setError('');
    setSubmitSuccess(false);
    setIsSubmitting(false);
  };

  const handleClose = () => {
    onClose();
    resetForm();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
        {submitSuccess ? (
          <div className="p-6 text-center">
            <div className="text-green-500 text-5xl mb-4">✓</div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Report Submitted
            </h3>
            <p className="text-gray-600 dark:text-gray-300">
              Thank you for helping keep our community safe. We'll review your report and take appropriate action.
            </p>
          </div>
        ) : (
          <>
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Report {contentType === 'post' ? 'Post' : contentType === 'comment' ? 'Comment' : 'User'}
                </h3>
                <button
                  onClick={handleClose}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  ✕
                </button>
              </div>
              {contentPreview && (
                <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-700 rounded text-sm text-gray-700 dark:text-gray-300 border-l-4 border-red-400">
                  <div className="font-medium mb-1">Reporting this content:</div>
                  <div className="truncate">{contentPreview}</div>
                </div>
              )}
            </div>

            <form onSubmit={handleSubmit} className="p-6">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Why are you reporting this {contentType}?
                </label>
                <div className="space-y-2">
                  {reportCategories.map((category) => (
                    <label key={category.value} className="flex items-start gap-3 p-3 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer">
                      <input
                        type="radio"
                        name="category"
                        value={category.value}
                        checked={reportCategory === category.value}
                        onChange={(e) => setReportCategory(e.target.value)}
                        className="mt-1 text-red-600 focus:ring-red-500"
                      />
                      <div>
                        <div className="font-medium text-gray-900 dark:text-white">
                          {category.label}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {category.description}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Detailed Reason (Required - min 10 characters)
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Please explain why you're reporting this content. Be specific about what violates our community guidelines..."
                  rows={3}
                  maxLength={500}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  required
                />
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {description.length}/500 characters (minimum 10 required)
                </div>
              </div>

              {error && (
                <div className="mb-4 p-3 bg-red-100 border border-red-200 text-red-700 rounded-md text-sm">
                  {error}
                </div>
              )}

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={handleClose}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting || !reportCategory || description.trim().length < 10}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Report'}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default ContentReportModal;